import countriesJson from 'countries-states-cities/dist/lib/countries.json';
import statesJson from 'countries-states-cities/dist/lib/states.json';
import type { ICountry, IState } from 'countries-states-cities';
import type { AddressLike } from './addressFormat';

// Importing the two datasets directly keeps the package's 36MB cities.json out of
// the bundle — its index.js eagerly requires all three.
export const allCountries = countriesJson as ICountry[];
const allStates = statesJson as IState[];

const countryByIso3 = new Map(
  allCountries.map((country) => [country.iso3, country])
);
const statesByCountryId = new Map<number, IState[]>();

function statesOfCountry(countryId: number): IState[] {
  const cached = statesByCountryId.get(countryId);
  if (cached) return cached;
  const states = allStates
    .filter((state) => state.country_id === countryId)
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  statesByCountryId.set(countryId, states);
  return states;
}

export function getStatesForCountryIso3(iso3: string): IState[] {
  const matchedCountry = countryByIso3.get(iso3);
  return matchedCountry ? statesOfCountry(matchedCountry.id) : [];
}

export const COUNTRY_OPTIONS = allCountries.map((country) => ({
  label: country.name,
  value: country.iso3,
}));

export function iso3ToCountryName(iso3: string): string {
  return countryByIso3.get(iso3)?.name ?? iso3;
}

export function stateCodeToRegionName(
  iso3: string,
  stateCode: string
): string | null {
  if (!stateCode) return null;
  return (
    getStatesForCountryIso3(iso3).find(
      (state) => state.state_code === stateCode
    )?.name ?? stateCode
  );
}

export type AddressFormState = {
  addressLine1: string;
  addressLine2: string;
  country: string;
  region: string;
  city: string;
  zipCode: string;
};

export const EMPTY_ADDRESS_FORM: AddressFormState = {
  addressLine1: '',
  addressLine2: '',
  country: '',
  region: '',
  city: '',
  zipCode: '',
};

export function isAddressFormComplete(
  form: AddressFormState,
  requireZip: boolean
): boolean {
  const hasStates = getStatesForCountryIso3(form.country).length > 1;
  return Boolean(
    form.addressLine1.trim() &&
    form.city.trim() &&
    form.country &&
    (!requireZip || form.zipCode.trim()) &&
    (!hasStates || form.region)
  );
}

type ResolvedFields = {
  addressLine1?: string | null;
  city?: string | null;
  region?: string | null;
  countryCode?: string | null;
  zipCode?: string | null;
};

export function resolvedToFormPatch(
  resolved: ResolvedFields,
  current: AddressFormState
): AddressFormState {
  const resolvedCountry = COUNTRY_OPTIONS.find(
    (option) => option.value === resolved.countryCode
  )?.value;
  const countryIso3 = resolvedCountry ?? current.country;
  const resolvedStateCode = resolved.region
    ? getStatesForCountryIso3(countryIso3).find(
        (state) => state.name === resolved.region
      )?.state_code
    : undefined;
  return {
    ...current,
    addressLine1: resolved.addressLine1 ?? current.addressLine1,
    city: resolved.city ?? current.city,
    region:
      resolvedStateCode ??
      (countryIso3 === current.country ? current.region : ''),
    country: countryIso3,
    zipCode: resolved.zipCode ?? current.zipCode,
  };
}

export function addressToFormState(address: AddressLike): AddressFormState {
  const matchedCountry = allCountries.find(
    (country) => country.name === address.country
  );
  const countryIso3 = matchedCountry?.iso3 ?? address.country ?? '';
  let regionCode = '';
  if (address.region && matchedCountry) {
    regionCode =
      statesOfCountry(matchedCountry.id).find(
        (state) => state.name === address.region
      )?.state_code ?? address.region;
  }
  return {
    addressLine1: address.addressLine1 ?? '',
    addressLine2: address.addressLine2 ?? '',
    country: countryIso3,
    region: regionCode,
    city: address.city ?? '',
    zipCode: address.zipCode ?? '',
  };
}
